<div class="page-container">
  <div class="card">
    <h1 class="card-title">About LogMerge</h1>
    <hr class="divider">

    <div class="about-content">

      <!-- Business Problem -->
      <section class="about-section">
        <h2>Overview</h2>
        <p>
          Before drilling a well, subsurface teams need to understand what rock layers exist, where hydrocarbons are, and how porous the rock is.
          Measurement tools lowered into the borehole record this data as <strong>LAS files</strong> -one per tool run, one per well.
          A large field can produce thousands of files spanning decades, from multiple service companies, in inconsistent formats.
        </p>
        <p><strong>The fundamental problem: you cannot do any analysis until all this data is in one clean, consistent format.</strong></p>

        <div class="measurement-grid">
          <div class="measurement-card">
            <div class="meas-label">Gamma Ray (GR)</div>
            <div class="meas-desc">Distinguishes sand (reservoir) from shale (non-reservoir)</div>
          </div>
          <div class="measurement-card">
            <div class="meas-label">Resistivity</div>
            <div class="meas-desc">High resistance = oil · Low resistance = water</div>
          </div>
          <div class="measurement-card">
            <div class="meas-label">Neutron Porosity (NPHI)</div>
            <div class="meas-desc">How much pore space exists in the rock</div>
          </div>
          <div class="measurement-card">
            <div class="meas-label">Density (RHOB)</div>
            <div class="meas-desc">Rock density — used to calculate porosity</div>
          </div>
        </div>
      </section>

      <!-- Pipeline -->
      <section class="about-section">
        <h2>Log Merge Workflow</h2>
        <div class="stages-grid">

          <div class="stage-card">
            <div class="stage-header">
              <div class="stage-number">1</div>
              <h3>Log Suite Mapping</h3>
            </div>
            <p>Maps variant mnemonic names (GR, HCGR, SGR) to a single standard so the same measurement is recognised across all files.</p>
          </div>

          <div class="stage-card">
            <div class="stage-header">
              <div class="stage-number">2</div>
              <h3>Clean</h3>
            </div>
            <p>Renames files by UWI, fixes legacy WRAP format, corrects depth order, standardizes null values, and removes empty or malformed columns.</p>
          </div>

          <div class="stage-card">
            <div class="stage-header">
              <div class="stage-number">3</div>
              <h3>Resample</h3>
            </div>
            <p>Interpolates all files onto a common depth grid so measurements from different tool runs align row-for-row.</p>
          </div>

          <div class="stage-card">
            <div class="stage-header">
              <div class="stage-number">4</div>
              <h3>Combine</h3>
            </div>
            <p>Joins all LAS files for a well into one wide table — every measurement side-by-side at every depth.</p>
          </div>

          <div class="stage-card wide">
            <div class="stage-header">
              <div class="stage-number">5</div>
              <h3>Merge Priority Waterfall</h3>
            </div>
            <p>
              Collapses duplicate measurements into one complete column. At each depth it picks the highest-priority sensor;
              if null, falls back to the next best — producing a gap-free log.
              3,000 files processed in minutes with full source provenance tracked.
            </p>
          </div>

        </div>
      </section>

      <!-- Big Picture -->
      <section class="about-section highlight-section">
        <h2>Summary</h2>
        <p>
          Subsurface data is the most expensive data in the world to collect-a single well can cost <strong>$10–100 million</strong> to drill.
          Yet historically this data sits in thousands of inconsistent files that no system can read in bulk.
          LogMerge turns that raw, fragmented field data into a clean, analysis-ready dataset automatically -
          so petrophysicists spend time on interpretation, not data wrangling.
        </p>
      </section>

      <!-- Tech Stack -->
      <section class="about-section">
        <h2>Technology Stack</h2>
        <div class="tech-grid">
          <span class="tech-badge">Python</span>
          <span class="tech-badge">Angular</span>
          <span class="tech-badge">FastAPI</span>
          <span class="tech-badge">NumPy</span>
          <span class="tech-badge">Azure Functions</span>
          <span class="tech-badge">Azure DevOps</span>
        </div>
      </section>

    </div>
  </div>
</div>
